import { SchemaError, SchemaWarning } from '@rehaje/errors'

import type { Locales } from '@rehaje/errors'

export class SchemaAsyncFunctionError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field is not an asynchronous function',
    'zh-CN': '%s 字段不是异步函数',
    'zh-HK': '%s 字段不是異步函數'
  }

  override name = 'SchemaAsyncFunctionError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaCustomError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s',
    'zh-CN': '%s',
    'zh-HK': '%s'
  }

  override name = 'SchemaCustomError' as const

  constructor(message: string) {
    super(message)
  }
}

export class SchemaDeprecatedWarning extends SchemaWarning {
  static override locales: Locales = {
    'en-US': '%s field is deprecated',
    'zh-CN': '%s 字段已经废弃',
    'zh-HK': '%s 字段已經廢棄'
  }

  override name = 'SchemaDeprecatedWarning' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaFormatError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field and format %s do not match',
    'zh-CN': '%s 字段和格式 %s 不匹配',
    'zh-HK': '%s 字段和格式 %s 不匹配'
  }

  override name = 'SchemaFormatError' as const

  constructor(field = 'anonymous', format: string) {
    super(field, format)
  }
}

export class SchemaFunctionParametersError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field function protocol is missing parameters',
    'zh-CN': '%s 字段函数协议缺少参数',
    'zh-HK': '%s 字段函數協議缺少參數'
  }

  override name = 'SchemaFunctionParametersError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaGeneratorFunctionError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field is not a generator function',
    'zh-CN': '%s 字段不是生成器函数',
    'zh-HK': '%s 字段不是產生器函數'
  }

  override name = 'SchemaGeneratorFunctionError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaIdempotenceError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field is not idempotent',
    'zh-CN': '%s 字段不是幂等的',
    'zh-HK': '%s 字段不是幂等的'
  }

  override name = 'SchemaIdempotenceError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaIntersectionInferError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field cannot be inferred to Schema.Object type',
    'zh-CN': '%s 字段无法被推导成 Schema.Object 类型',
    'zh-HK': '%s 字段無法被推導成 Schema.Object 類型'
  }

  override name = 'SchemaIntersectionInferError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaIntersectionTypesError extends SchemaError {
  static override locales: Locales = {
    'en-US':
      '%s field only supports Schema.Intersection and Schema.Object as child nodes',
    'zh-CN': '%s 字段只支持 Schema.Intersection 和 Schema.Object 作为子节点',
    'zh-HK': '%s 字段只支持 Schema.Intersection 和 Schema.Object 作為子節點'
  }

  override name = 'SchemaIntersectionTypesError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaMaximumError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field value cannot be greater than %s',
    'zh-CN': '%s 字段数值不能大于 %s',
    'zh-HK': '%s 字段數值不能大於 %s'
  }

  override name = 'SchemaMaximumError' as const

  constructor(field = 'anonymous', value: bigint | number) {
    super(field, String(value))
  }
}

export class SchemaMaxItemsError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field array item cannot be greater than %s',
    'zh-CN': '%s 字段数组项不能超过 %s 个',
    'zh-HK': '%s 字段數組項不能超過 %s 個'
  }

  override name = 'SchemaMaxItemsError' as const

  constructor(field = 'anonymous', size: number) {
    super(field, String(size))
  }
}

export class SchemaMaxLengthError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field length cannot be greater than %s',
    'zh-CN': '%s 字段长度不能大于 %s',
    'zh-HK': '%s 字段長度不能大於 %s'
  }

  override name = 'SchemaMaxLengthError' as const

  constructor(field = 'anonymous', length: number) {
    super(field, String(length))
  }
}

export class SchemaMaxPropertiesError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field object member cannot be greater than %s',
    'zh-CN': '%s 字段对象成员不能超过 %s 个',
    'zh-HK': '%s 字段對象成員不能超過 %s 個'
  }

  override name = 'SchemaMaxPropertiesError' as const

  constructor(field = 'anonymous', size: number) {
    super(field, String(size))
  }
}

export class SchemaMinimumError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field value cannot be less than %s',
    'zh-CN': '%s 字段数值不能小于 %s',
    'zh-HK': '%s 字段數值不能小於 %s'
  }

  override name = 'SchemaMinimumError' as const

  constructor(field = 'anonymous', value: bigint | number) {
    super(field, String(value))
  }
}

export class SchemaMinItemsError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field array item cannot be less than %s',
    'zh-CN': '%s 字段数组项不能少于 %s 个',
    'zh-HK': '%s 字段數組項不能少於 %s 個'
  }

  override name = 'SchemaMinItemsError' as const

  constructor(field = 'anonymous', size: number) {
    super(field, String(size))
  }
}

export class SchemaMinLengthError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field length cannot be less than %s',
    'zh-CN': '%s 字段长度不能小于 %s',
    'zh-HK': '%s 字段長度不能小於 %s'
  }

  override name = 'SchemaMinLengthError' as const

  constructor(field = 'anonymous', length: number) {
    super(field, String(length))
  }
}

export class SchemaMinPropertiesError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field object member cannot be less than %s',
    'zh-CN': '%s 字段对象成员不能少于 %s 个',
    'zh-HK': '%s 字段對象成員不能少於 %s 個'
  }

  override name = 'SchemaMinPropertiesError' as const

  constructor(field = 'anonymous', size: number) {
    super(field, String(size))
  }
}

export class SchemaPatternError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field does not match regular expression %s',
    'zh-CN': '%s 字段不符合正则表达式 %s',
    'zh-HK': '%s 字段不符合正則表達式 %s'
  }

  override name = 'SchemaPatternError' as const

  constructor(field = 'anonymous', pattern: string) {
    super(field, pattern)
  }
}

export class SchemaRequiredError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field is required',
    'zh-CN': '%s 字段必填',
    'zh-HK': '%s 字段必填'
  }

  override name = 'SchemaRequiredError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaTransformError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field transform failed',
    'zh-CN': '%s 字段转换失败',
    'zh-HK': '%s 字段轉換失敗'
  }

  override name = 'SchemaTransformError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaTypeError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field type does not match',
    'zh-CN': '%s 字段类型不匹配',
    'zh-HK': '%s 字段類型不匹配'
  }

  override name = 'SchemaTypeError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaUnionEmptyError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field type is missing union type',
    'zh-CN': '%s 字段类型缺少联合类型',
    'zh-HK': '%s 字段類型缺少聯合類型'
  }

  override name = 'SchemaUnionEmptyError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}

export class SchemaUnionTypesError extends SchemaError {
  static override locales: Locales = {
    'en-US': '%s field type does not match any union type',
    'zh-CN': '%s 字段类型不匹配任意联合类型',
    'zh-HK': '%s 字段類型不匹配任意聯合類型'
  }

  override name = 'SchemaUnionTypesError' as const

  constructor(field = 'anonymous') {
    super(field)
  }
}
